import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { TeamProvider } from "@/lib/teams";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AppLayout from "@/components/AppLayout";
import DashboardRouter from "@/pages/DashboardRouter";
import PlayersPage from "@/pages/PlayersPage";
import ConvocationsPage from "@/pages/ConvocationsPage";
import AttendancePage from "@/pages/AttendancePage";
import TrainingsPage from "@/pages/TrainingsPage";
import TacticalBoardPage from "@/pages/TacticalBoardPage";
import CalendarPage from "@/pages/CalendarPage";
import StatsPage from "@/pages/StatsPage";
import MembersPage from "@/pages/MembersPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import FieldsPage from "@/pages/FieldsPage";
import FieldCalendarPage from "@/pages/FieldCalendarPage";
import ExerciseLibraryPage from "@/pages/ExerciseLibraryPage";
import StaffPage from "@/pages/StaffPage";
import AdminPage from "@/pages/AdminPage";
import AnalysisPage from "@/pages/AnalysisPage";
import ScoutingPage from "@/pages/ScoutingPage";
import TrustCenterPage from "@/pages/TrustCenterPage";
import PwaInstallBanner from "@/components/PwaInstallBanner";
import NotificationsPage from "@/pages/NotificationsPage";
import ParentsPage from "@/pages/ParentsPage";
import TournamentsPage from "@/pages/TournamentsPage";
import ModulisticaPage from "@/pages/ModulisticaPage";
import PublicEnrollmentPage from "@/pages/PublicEnrollmentPage";
import MisterAIPage from "@/pages/MisterAIPage";
import PaymentResultPage from "@/pages/PaymentResultPage";
import ClubManagementPage from "@/pages/ClubManagementPage";
import ClubPublicPage from "@/pages/ClubPublicPage";
import PublicAttendancePage from "@/pages/PublicAttendancePage";
import PricingPage from "@/pages/PricingPage";
import BillingPage from "@/pages/BillingPage";
import CouponsAdminPage from "@/pages/CouponsAdminPage";
import ChatPage from "@/pages/ChatPage";
import SignaturesAuditPage from "@/pages/SignaturesAuditPage";
import BillingLifecyclePage from "@/pages/BillingLifecyclePage";

/** Root redirect:
 *  - Unauthenticated users see the public marketing LandingPage.
 *  - Authenticated users are redirected to /app (the dashboard).
 */
function RootGate() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        Caricamento...
      </div>
    );
  }
  if (user && user !== false) return <Navigate to="/app" replace />;
  return <LandingPage />;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        Caricamento...
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TeamProvider>
          <Routes>
            {/* Public marketing landing page (smart-redirects authenticated users to /app) */}
            <Route path="/" element={<RootGate />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/invito/:token" element={<AcceptInvitePage />} />
            <Route path="/iscrizione/:token" element={<PublicEnrollmentPage />} />
            <Route path="/public/enroll/:token" element={<PublicEnrollmentPage />} />
            <Route path="/club/:slug" element={<ClubPublicPage />} />
            <Route path="/presenza/:token" element={<PublicAttendancePage />} />
            <Route path="/legal/trust" element={<TrustCenterPage />} />
            <Route path="/trust" element={<Navigate to="/legal/trust" replace />} />

            {/* Authenticated application */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardRouter />} />
              <Route path="anagrafica" element={<PlayersPage />} />
              <Route path="convocazioni" element={<ConvocationsPage />} />
              <Route path="presenze" element={<AttendancePage />} />
              <Route path="allenamenti" element={<TrainingsPage />} />
              <Route path="lavagna" element={<TacticalBoardPage />} />
              <Route path="calendario" element={<CalendarPage />} />
              <Route path="statistiche" element={<StatsPage />} />
              <Route path="membri" element={<MembersPage />} />
              <Route path="campi" element={<FieldsPage />} />
              <Route path="campi/calendario" element={<FieldCalendarPage />} />
              <Route path="libreria" element={<ExerciseLibraryPage />} />
              <Route path="staff" element={<StaffPage />} />
              <Route path="amministrazione" element={<AdminPage />} />
              <Route path="analisi" element={<AnalysisPage />} />
              <Route path="scouting" element={<ScoutingPage />} />
              <Route path="notifiche" element={<NotificationsPage />} />
              <Route path="genitori" element={<ParentsPage />} />
              <Route path="tornei" element={<TournamentsPage />} />
              <Route path="modulistica" element={<ModulisticaPage />} />
              <Route path="mister-ai" element={<MisterAIPage />} />
              <Route path="club" element={<ClubManagementPage />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="billing/result" element={<PaymentResultPage />} />
              <Route path="admin/coupons" element={<CouponsAdminPage />} />
              <Route path="admin/billing/lifecycle" element={<BillingLifecyclePage />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="firme" element={<SignaturesAuditPage />} />
              <Route path="pagamenti/esito" element={<PaymentResultPage />} />
            </Route>

            {/* Legacy redirects (preserve old bookmarks, emails, PWA shortcuts) */}
            <Route path="/anagrafica" element={<Navigate to="/app/anagrafica" replace />} />
            <Route path="/convocazioni" element={<Navigate to="/app/convocazioni" replace />} />
            <Route path="/presenze" element={<Navigate to="/app/presenze" replace />} />
            <Route path="/allenamenti" element={<Navigate to="/app/allenamenti" replace />} />
            <Route path="/lavagna" element={<Navigate to="/app/lavagna" replace />} />
            <Route path="/calendario" element={<Navigate to="/app/calendario" replace />} />
            <Route path="/statistiche" element={<Navigate to="/app/statistiche" replace />} />
            <Route path="/membri" element={<Navigate to="/app/membri" replace />} />
            <Route path="/campi" element={<Navigate to="/app/campi" replace />} />
            <Route path="/campi/calendario" element={<Navigate to="/app/campi/calendario" replace />} />
            <Route path="/libreria" element={<Navigate to="/app/libreria" replace />} />
            <Route path="/staff" element={<Navigate to="/app/staff" replace />} />
            <Route path="/amministrazione" element={<Navigate to="/app/amministrazione" replace />} />
            <Route path="/analisi" element={<Navigate to="/app/analisi" replace />} />
            <Route path="/scouting" element={<Navigate to="/app/scouting" replace />} />
            <Route path="/notifiche" element={<Navigate to="/app/notifiche" replace />} />
            <Route path="/genitori" element={<Navigate to="/app/genitori" replace />} />
            <Route path="/tornei" element={<Navigate to="/app/tornei" replace />} />
            <Route path="/modulistica" element={<Navigate to="/app/modulistica" replace />} />
            <Route path="/mister-ai" element={<Navigate to="/app/mister-ai" replace />} />
            <Route path="/club" element={<Navigate to="/app/club" replace />} />
            <Route path="/pricing" element={<Navigate to="/app/pricing" replace />} />
            <Route path="/billing" element={<Navigate to="/app/billing" replace />} />
            <Route path="/billing/result" element={<Navigate to="/app/billing/result" replace />} />
            <Route path="/admin/coupons" element={<Navigate to="/app/admin/coupons" replace />} />
            <Route path="/admin/billing/lifecycle" element={<Navigate to="/app/admin/billing/lifecycle" replace />} />
            <Route path="/chat" element={<Navigate to="/app/chat" replace />} />
            <Route path="/firme" element={<Navigate to="/app/firme" replace />} />
            <Route path="/pagamenti/esito" element={<Navigate to="/app/pagamenti/esito" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
          <PwaInstallBanner />
        </TeamProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
